import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import ConfirmModal from "../../../../components/ConfirmModal";
import { KeepoLoader } from "../../../../components/KeepoLoader";
import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusPromo } from "../../../../components/PlusPromo";
import { SubHeader } from "../../../../components/SubHeader";
import { useAppState } from "../../../../contexts/App";
import { hasActiveSubscription } from "../../../../services/api";
import { isApiError } from "../../../../services/api/errors";
import { CustomDomain, customDomainService } from "../../../../services/customDomain";
import { connectDomainKeepo, FROM_PRICE } from "../../../../services/domains";
import { formatMoney } from "../../../../utils/money";
import { AddDomain } from "./components/AddDomain";
import { ConnectedDomain } from "./components/ConnectedDomain";
import { SetupDomain } from "./components/SetupDomain";
import { Card, Container, Section } from "./styles";

// How often the DNS is checked again while waiting for the records
const CHECK_INTERVAL = 4000;
// How often a domain bought at Keepo is verified again while Keepo connects it
export const KEEPO_DOMAIN_CHECK_INTERVAL = 15000;
// Back from buying a domain here: how often it's checked while it's registered (the server then connects it),
// and for how long at most (then the page is as usual: the domain can be connected from the list)
export const JUST_BOUGHT_CHECK_INTERVAL = 4000;
const JUST_BOUGHT_MAX_CHECKS = 30;

// Backend error codes (api/models/ConnectCustomDomainForm.php) mapped to a specific
// copy; anything else (including a network error) falls back to `errors.generic`.
const CONNECT_ERROR_KEYS: Record<string, string> = {
  DOMAIN_ALREADY_TAKEN: "errors.alreadyTaken",
  INVALID_DOMAIN: "errors.invalidDomain",
  DOMAIN_NOT_ALLOWED: "errors.notAllowed",
  CUSTOM_DOMAIN_ALREADY_EXISTS: "errors.alreadyExists",
  CUSTOM_DOMAIN_REMOVAL_IN_PROGRESS: "errors.removalInProgress",
  PLUS_REQUIRED: "errors.plusRequired",
};

/**
 * New in-app custom domain flow (PLUS): add the domain -> create the DNS records -> connected.
 * Lives next to the old "Domínio Personalizado" link (a Google Form) until it replaces it.
 */
export const CustomDomainPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { appState } = useAppState();
  const siteId = appState.editingSiteId as string;

  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState<CustomDomain>();
  const [errorMessage, setErrorMessage] = useState<string>();
  // The domain just bought from this screen (the checkout comes back here): being registered, then connected
  const justBought = (useLocation().state as { justBought?: string } | null)?.justBought;
  const [justBoughtChecks, setJustBoughtChecks] = useState(0);
  const isWaitingBought = !!justBought && !domain && justBoughtChecks < JUST_BOUGHT_MAX_CHECKS;

  const prefix = "pages.settings.customDomain";

  useEffect(() => {
    customDomainService
      .get(siteId)
      .then(value => {
        setDomain(value);
        setErrorMessage(undefined);
      })
      .catch(() => setErrorMessage(t(`${prefix}.errors.generic`)))
      .finally(() => setLoading(false));
  }, [siteId, t]);

  // While waiting for the records, keep checking until every one is found. A failed
  // check surfaces an error and stops this loop (the effect only re-arms when `domain`
  // changes) — the user can restart it from SetupDomain's "Já adicionei os registros".
  useEffect(() => {
    if (domain?.status !== "verifying") {
      return;
    }
    const timer = setTimeout(() => {
      customDomainService
        .verify(siteId)
        .then(setDomain)
        .catch(() => setErrorMessage(t(`${prefix}.errors.generic`)));
    }, CHECK_INTERVAL);
    return () => clearTimeout(timer);
  }, [domain, siteId, t]);

  // A domain bought at Keepo, until it's connected: asked to be verified now and then while the screen is open
  // (DNS, then nginx and the certificate move along as soon as they can, not only on the server's own rounds);
  // nothing for the person to confirm. A failed check just waits for the next one
  useEffect(() => {
    if (!domain?.managedByKeepo || domain.status === "connected") {
      return;
    }
    const timer = setTimeout(() => {
      customDomainService
        .verify(siteId)
        .then(setDomain)
        .catch(() => setDomain(current => current && { ...current }));
    }, KEEPO_DOMAIN_CHECK_INTERVAL);
    return () => clearTimeout(timer);
  }, [domain, siteId]);

  // Registered and connected by the server a few seconds after paying: checked until this Keepo has it
  useEffect(() => {
    if (loading || !isWaitingBought) {
      return;
    }
    const timer = setTimeout(() => {
      customDomainService
        .get(siteId)
        .then(value => (value ? setDomain(value) : setJustBoughtChecks(count => count + 1)))
        .catch(() => setJustBoughtChecks(count => count + 1));
    }, JUST_BOUGHT_CHECK_INTERVAL);
    return () => clearTimeout(timer);
  }, [loading, isWaitingBought, justBoughtChecks, siteId]);

  const connect = useCallback(
    async (value: string, provider: string) => {
      setErrorMessage(undefined);
      try {
        setDomain(await customDomainService.connect(siteId, value, provider));
      } catch (e) {
        const code = Object.keys(CONNECT_ERROR_KEYS).find(candidate => isApiError(e, candidate));
        setErrorMessage(t(`${prefix}.${code ? CONNECT_ERROR_KEYS[code] : "errors.generic"}`));
      }
    },
    [siteId, t]
  );

  // A domain bought at Keepo: Keepo sets its DNS and adds it as this Keepo's custom domain; then its status
  const connectKeepoDomain = useCallback(
    async (value: string) => {
      setErrorMessage(undefined);
      await connectDomainKeepo(value, siteId);
      // It is connected: only reading its state back can fail now, and that is asked again (the person would be
      // told "could not connect" for something that worked)
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          setDomain(await customDomainService.get(siteId));
          return;
        } catch {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }
    },
    [siteId]
  );

  const confirmRecords = useCallback(async () => {
    setErrorMessage(undefined);
    setDomain(current => current && { ...current, status: "verifying" });
    try {
      setDomain(await customDomainService.verify(siteId));
    } catch {
      setErrorMessage(t(`${prefix}.errors.generic`));
    }
  }, [siteId, t]);

  const disconnect = useCallback(async () => {
    try {
      await customDomainService.disconnect(siteId);
      setDomain(undefined);
      setErrorMessage(undefined);
    } catch {
      setErrorMessage(t(`${prefix}.errors.generic`));
    }
  }, [siteId, t]);

  if (!hasActiveSubscription(appState.editingSiteSubscription)) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <Container>
      <SubHeader title={t(`${prefix}.title`)} crumbs={[{ label: t("pages.settings.header.title"), to: "/settings" }]} />
      {/* Selling a domain, always (as the PLUS bar on /sites): bought from here, it connects to this Keepo */}
      {!loading && (
        <Section>
          <PlusPromo
            compact
            tag={t(`${prefix}.promo.tag`)}
            title={t(`${prefix}.promo.title`, {
              value: formatMoney(Math.round(FROM_PRICE * 100), "brl", i18n.language),
            })}
            description={t(`${prefix}.promo.text`)}
            cta={t(`${prefix}.promo.cta`)}
            onClick={() => navigate(`/domains?site=${encodeURIComponent(siteId)}`)}
          />
        </Section>
      )}
      {/* What went wrong (connecting, verifying, disconnecting): in a modal, not loose over the page */}
      {errorMessage && (
        <ConfirmModal
          type="INFO"
          title={t(`${prefix}.errors.title`)}
          descriptions={[errorMessage]}
          confirmButtonTitle={t(`${prefix}.errors.ok`)}
          onConfirm={() => setErrorMessage(undefined)}
          onExit={() => setErrorMessage(undefined)}
        />
      )}
      {loading ? (
        <LoadingModal />
      ) : isWaitingBought ? (
        <Section>
          <Card aria-busy="true">
            <h3>{t(`${prefix}.keepoDomain.registering.title`, { domain: justBought })}</h3>
            <p className="lead">{t(`${prefix}.keepoDomain.registering.text`)}</p>
            <span className="loader">
              <KeepoLoader size={24} color="var(--color-neutral-white)" />
            </span>
          </Card>
        </Section>
      ) : !domain ? (
        <AddDomain siteId={siteId} onConnect={connect} onConnectKeepoDomain={connectKeepoDomain} />
      ) : domain.status === "connected" ? (
        <ConnectedDomain domain={domain} onDisconnect={disconnect} />
      ) : (
        <SetupDomain domain={domain} onConfirm={confirmRecords} onChangeDomain={disconnect} />
      )}
    </Container>
  );
};
